import type { JsSnackbarSeverity } from '@/app/quadratic-core-types';
import { toast } from '@/shared/shadcn/ui/sonner';
import type { JSX } from 'react';
import { createContext, useCallback, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router';

export const SNACKBAR_DURATION = 6000;
export const snackbarMsgQueryParam = 'snackbar-msg';
export const snackbarSeverityQueryParam = 'snackbar-severity';

/// TODO: this should be centralized in a single place
const DEFINED_MESSAGES: Record<string, string> = {
  delete_rows_error: 'Cannot delete rows containing table names or columns',
};

/**
 * Context
 */

export interface SnackbarOptions {
  severity?: JsSnackbarSeverity;
  button?: { title: string; callback: Function };
  stayOpen?: boolean;
}

export interface GlobalSnackbar {
  addGlobalSnackbar: (message: string | JSX.Element, options?: SnackbarOptions) => void;
  closeCurrentSnackbar: () => void;
}
const defaultContext: GlobalSnackbar = {
  addGlobalSnackbar: () => {
    console.warn(
      '[GlobalSnackbarContext] `addGlobalSnackbar` was fired before it was initialized with a default value.'
    );
  },
  closeCurrentSnackbar: () => {
    console.warn(
      '[GlobalSnackbarContext] `closeCurrentSnackbar` was fired before it was initialized with a default value.'
    );
  },
};
export const GlobalSnackbarContext = createContext(defaultContext);

/**
 * Consumer
 */

export const useGlobalSnackbar: () => GlobalSnackbar = () => useContext(GlobalSnackbarContext);

/**
 * Provider
 */

export function GlobalSnackbarProvider({ children }: { children: React.ReactElement }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const addGlobalSnackbar: GlobalSnackbar['addGlobalSnackbar'] = useCallback(
    (message: string | JSX.Element, options?: SnackbarOptions) => {
      const resolvedMessage = typeof message === 'string' ? (DEFINED_MESSAGES[message] ?? message) : message;

      const toastOptions: Parameters<typeof toast>[1] = {
        duration: options?.stayOpen ? Infinity : SNACKBAR_DURATION,
        dismissible: true,
      };

      if (options?.button) {
        const { title, callback } = options.button;
        toastOptions.action = {
          label: title,
          onClick: () => callback(),
        };
      }

      switch (options?.severity) {
        case 'error':
          toast.error(resolvedMessage, toastOptions);
          break;
        case 'warning':
          toast.warning(resolvedMessage, toastOptions);
          break;
        case 'success':
          toast.success(resolvedMessage, toastOptions);
          break;
        default:
          toast(resolvedMessage, toastOptions);
      }
    },
    []
  );

  // Dismisses all currently visible toasts. Sonner can show multiple toasts
  // at once, so "current" here means any visible snackbar (not just the most
  // recently added one).
  const closeCurrentSnackbar: GlobalSnackbar['closeCurrentSnackbar'] = useCallback(() => {
    toast.dismiss();
  }, []);

  const value: GlobalSnackbar = { addGlobalSnackbar, closeCurrentSnackbar };

  // If the route has these query params (when it loads), we'll throw up a snackbar too
  useEffect(() => {
    const msg = searchParams.get(snackbarMsgQueryParam);
    const severity = searchParams.get(snackbarSeverityQueryParam);

    if (msg) {
      addGlobalSnackbar(msg, severity ? { severity: 'error' } : undefined);
      searchParams.delete(snackbarMsgQueryParam);
      searchParams.delete(snackbarSeverityQueryParam);
      setSearchParams(searchParams, { replace: true });
    }
  }, [addGlobalSnackbar, searchParams, setSearchParams]);

  return <GlobalSnackbarContext.Provider value={value}>{children}</GlobalSnackbarContext.Provider>;
}
